import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ReposicionService } from '../../../../reposicion.service';
import { BranchStoreService } from '../../../branch-store.service';

import {
  Inventario,
  ProductoPayload,
  BranchPayload,
  ReposicionPayload,
  BranchStore
} from '../../../../models/reposicion.model';

@Component({
  selector: 'app-acciones-reposicion',
  templateUrl: './acciones-reposicion.component.html',
  styleUrls: ['./acciones-reposicion.component.css'],
  providers: [MessageService, ConfirmationService]
})
export class AccionesReposicionComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  sucursalesSeleccionadas: string[] = [];

  branchMap = new Map<string, number>();

  loading = false;

  constructor(
    private reposicionService: ReposicionService,
    private branchStoreService: BranchStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.reposicionService.sucursalesSeleccionadas$
      .pipe(takeUntil(this.destroy$))
      .subscribe((sucursales) => {
        this.sucursalesSeleccionadas = sucursales;
      });

    await this.cargarSucursales();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ===============================
        CARGAR SUCURSALES
  =============================== */

  async cargarSucursales(): Promise<void> {
    try {
      const resp = await this.branchStoreService.getByBusiness();

      const branches = (resp?.data ?? []) as BranchStore[];

      this.branchMap.clear();

      branches.forEach((b) => {
        this.branchMap.set(b.establishmentCode, Number(b.id));
      });
    } catch (error) {
      console.error('Error cargando sucursales', error);
    }
  }

  /* ===============================
        VOLVER
  =============================== */

  volverClick(): void {
    this.router.navigate(['dashboard'], {
      relativeTo: this.route.parent
    });
  }

  /* ===============================
        CONFIRMAR
  =============================== */

  confirmarClick(): void {
    if (this.loading) return;

    if (this.sucursalesSeleccionadas.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'Debes seleccionar al menos una sucursal'
      });
      return;
    }

    const excedeStock = this.reposicionService.validarStock(
      this.sucursalesSeleccionadas
    );

    if (excedeStock) {
      this.messageService.add({
        severity: 'error',
        summary: 'Stock insuficiente',
        detail: 'El total de reposición no puede superar el stock disponible'
      });
      return;
    }

    this.confirmationService.confirm({
      header: 'Confirmar reposición',
      message: '¿Estás seguro de confirmar la reposición?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmar',
      rejectLabel: 'Cancelar',

      accept: async () => {
        await this.procesarReposicion();

        await this.router.navigate(['dashboard'], {
          relativeTo: this.route.parent
        });
      }
    });
  }

  /* ===============================
        PROCESAR REPOSICION
  =============================== */

  async procesarReposicion(): Promise<void> {
    if (this.loading) return;

    this.loading = true;

    try {
      const inventario: Inventario[] = this.reposicionService.inventario;

      const branchProducts = new Map<string, Map<string, number>>();

      for (const item of inventario) {
        if (item.cantidad <= 0) continue;

        if (!this.sucursalesSeleccionadas.includes(item.sucursalCodigo))
          continue;

        let productMap = branchProducts.get(item.sucursalCodigo);

        if (!productMap) {
          productMap = new Map<string, number>();

          branchProducts.set(item.sucursalCodigo, productMap);
        }

        const current = productMap.get(item.productId) ?? 0;

        productMap.set(item.productId, current + item.cantidad);
      }

      if (branchProducts.size === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Sin productos',
          detail: 'Debes ingresar cantidades para la reposición'
        });

        this.loading = false;
        return;
      }

      const branches: BranchPayload[] = [];

      branchProducts.forEach((productsMap, sucursal) => {
        const branchId = this.branchMap.get(sucursal);

        if (!branchId) return;

        const products: ProductoPayload[] = [];

        productsMap.forEach((cantidad, productId) => {
          products.push({
            productId,
            formats: {
              Unidad: cantidad
            }
          });
        });

        branches.push({
          branchId,
          products
        });
      });

      const payload: ReposicionPayload = {
        branchMainId: 44,
        branches
      };

      console.log('REQUEST JSON');
      console.log(JSON.stringify(payload, null, 2));

      const response = await this.branchStoreService.createTransferBatch(
        payload
      );

      const errors = response?.data?.errors ?? [];

      if (errors.length > 0) {
        const detail = errors[0]?.details?.[0];

        this.messageService.add({
          severity: 'error',
          summary: 'Stock insuficiente',
          detail: detail?.message ?? 'Error en la reposición',
          life: 6000
        });

        this.loading = false;
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Reposición exitosa',
        detail: 'Redirigiendo...',
        life: 2000
      });

      this.reposicionService.limpiarInventario();
      this.reposicionService.limpiarSucursales();

      await new Promise((resolve) => setTimeout(resolve, 1500));

      /* REDIRECCIÓN AL DASHBOARD */

      this.router.navigate(['dashboard'], {
        relativeTo: this.route.parent
      });
    } catch (error) {
      console.error(error);

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo registrar la reposición'
      });
    } finally {
      this.loading = false;
    }
  }
}
